import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";

import { HeroCarousel } from "./HeroCarousel.js";
import { FestiveBadge } from "./ui/festive-badge.js";
import { FestiveChip } from "./ui/festive-chip.js";
import { useActiveFestival } from "../hooks/useActiveFestival.js";

/** The big "HRMS" heading is this many px in the 1440 px design frame. The carousel scales from it. */
const HRMS_FRAME_PX = 68;
const FALLBACK_SCALE = 1.3333;

/**
 * Product panel for the right of the sign-in page: the INTEGER label, the HRMS heading, a headline,
 * a sub-headline and the module carousel. The intro rows fade up in order (`.login-intro-row`, --i 1 to 6),
 * and the intro logo docks on the empty slot above the label. It is hidden below `lg`, where the sign-in
 * card takes the full width. `badgeReady` holds the festive icon back until the intro has docked the logo.
 */
export function LoginHero({ badgeReady }: { badgeReady: boolean }) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [scale, setScale] = useState(FALLBACK_SCALE);
  const festive = useActiveFestival();

  // Measured at runtime so the carousel stays in the same proportion to the heading at every size.
  useLayoutEffect(() => {
    const title = titleRef.current;
    if (!title) return;
    const measure = () => {
      const px = parseFloat(getComputedStyle(title).fontSize);
      setScale(px > 0 ? px / HRMS_FRAME_PX : FALLBACK_SCALE);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Runtime measurement, so it cannot be a static class. `--hx-s` is read by the `.hx-*` rules in tokens.css.
  // The negative margin (a share of this column's width) keeps an 80 px gap to the sign-in card at any width.
  return (
    <section
      aria-label="About Integer HRMS"
      style={{ "--hx-s": scale } as CSSProperties}
      className="hidden w-full max-w-[564px] flex-col lg:col-start-2 lg:flex lg:justify-self-start lg:ml-[calc(-50%_+_240px)]"
    >
      {/* The slot is what the intro logo docks on. The festive badge sits over its top-right corner, and
          the festive chip sits to its right in the empty row above INTEGER. Both are absolute, so nothing moves. */}
      <div data-logo-slot className="relative mb-[13px] h-9 w-9">
        {festive && badgeReady && (
          <FestiveBadge active={festive} className="left-[65%] top-[-35%] h-[75%] w-[75%]" />
        )}
        {festive && badgeReady && (
          <FestiveChip active={festive} className="absolute left-[48px] top-1/2 -translate-y-1/2" />
        )}
      </div>
      <p className="login-intro-row [--i:1] text-xs font-semibold uppercase tracking-[0.24em] text-surface-600">Integer</p>
      <h2 ref={titleRef} className="login-hero-title [--i:2] mt-2 text-6xl font-bold leading-none tracking-[-0.02em]">
        HRMS
      </h2>
      <div className="login-intro-row [--i:2] mt-5 h-px w-24 bg-brand-500" aria-hidden="true" />
      <h3 className="login-intro-row [--i:3] hx-headline">One platform for your entire workforce.</h3>
      <p className="login-intro-row [--i:4] hx-sub">
        From onboarding to payroll, every people process in one connected system.
      </p>
      <HeroCarousel />
    </section>
  );
}
